import React, { useCallback } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

// Components
import {
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Typography,
  Grid,
  FormHelperText,
  TextField,
  InputAdornment
} from '@mui/material'
import UserDropdown from './UserDropdown'
import { CustomController } from '../../common/CustomController'
import CheckboxController from '../../common/CheckboxController'

// Others
import { EditBillEntryForm, EditBillForm } from '../../../interfaceTypes/forms'
import { RemoveCircleOutline as RemoveIcon } from '@mui/icons-material'
import { formatCurrency } from '../../../utils'
import { flexCenter, flexJustifyEnd } from '../../../theme/styleConstants'
import { EntryType, FetchBillDetailsQuery, FeeType } from '../../../../generated'
import { useCategories } from '../../../hooks/useCategories'
import { calculateEntryTotal } from '../../../utils/billing'
import FeeTypeDropdown from '../../entries/FeeTypeDropdown'
import { CustomDatePickerController } from '../../common/CustomDatePickerRController'

type Case = FetchBillDetailsQuery['fetchBillDetails']['bill']['case']
type CaseUsers = NonNullable<Case>['caseToUsers']

interface EntryRowProps {
  fieldName: `${keyof Omit<EditBillForm, 'discountPercentage' | 'isPrePaid'>}.${number}`
  index: number
  isLast: boolean
  entryType: EntryType
  caseUsers?: CaseUsers | null
  isBillableDisabled: boolean
  onRemove: (index: number) => void
  initialFeeTypes?: FeeType[]
  skipInitialFeeTypeFetch?: boolean
}

const EntryRowComponent: React.FC<EntryRowProps> = ({
  fieldName,
  caseUsers,
  index,
  isLast,
  entryType,
  isBillableDisabled,
  onRemove,
  initialFeeTypes,
  skipInitialFeeTypeFetch
}) => {
  const { control, getValues, setValue } = useFormContext<EditBillForm>()
  const isExpenseEntry = entryType === EntryType.Expense

  const computeAmountFromQtyUnitPrice = useCallback(
    (newQty: number | null, newUnitPrice: number | null) => {
      if (newQty != null && newUnitPrice != null) {
        setValue(`${fieldName}.amount` as any, Math.round(newQty * newUnitPrice * 100) / 100)
      }
    },
    [setValue, fieldName]
  )

  const { categories } = useCategories({ fetchCategory: isExpenseEntry })

  const getCurrentEntry = (): EditBillEntryForm => {
    const formValues = getValues()
    const [arrayName, indexStr] = fieldName.split('.')
    const entryIndex = parseInt(indexStr)

    switch (arrayName) {
      case 'timeEntries':
        return formValues.timeEntries[entryIndex] || {}
      case 'legalFeeEntries':
        return formValues.legalFeeEntries[entryIndex] || {}
      case 'expenseEntries':
        return formValues.expenseEntries[entryIndex] || {}
      case 'investigatorEntries':
        return formValues.investigatorEntries[entryIndex] || {}
      default:
        return {} as EditBillEntryForm
    }
  }

  const currentEntry = getCurrentEntry()
  const isDisabled = !currentEntry.id

  return (
    <Box
      display="flex"
      alignItems="flex-start"
      gap={{ xs: 1, sm: 3 }}
      sx={{
        mb: isLast ? 0 : 3
      }}
    >
      <IconButton size="small" disabled={isDisabled} onClick={() => onRemove(index)}>
        <RemoveIcon fontSize="small" color="error" />
      </IconButton>

      <Grid container spacing={{ xs: 1, sm: 3 }} sx={{ minWidth: { xs: 0, md: 'auto' } }}>
        <Grid item xs={12} md={3}>
          <CustomDatePickerController
            controllerName={`${fieldName}.date`}
            controllerLabel="Date"
            sx={{ my: 0 }}
            isDisabled={isDisabled}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <UserDropdown
            name={`${fieldName}.userId`}
            control={control}
            disabled={isDisabled}
            caseUsers={caseUsers}
            entryType={entryType}
          />
        </Grid>

        {entryType === EntryType.Expense && (
          <Grid item xs={12} md={3}>
            <Controller
              name={`${fieldName}.categoryId`}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl fullWidth error={!!error} disabled={isDisabled}>
                  <InputLabel id="category-label">Category</InputLabel>
                  <Select {...field} label="Category" labelId="category-label">
                    {categories.length > 0 &&
                      categories.map(({ id, name }) => (
                        <MenuItem key={id} value={id}>
                          {name}
                        </MenuItem>
                      ))}
                  </Select>
                  {error && <FormHelperText>{error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>
        )}

        {entryType === EntryType.Legal && (
          <Grid item xs={12} md={3}>
            <FeeTypeDropdown
              name={`${fieldName}.feeTypeId`}
              control={control}
              disabled={isDisabled}
              initialFeeTypes={initialFeeTypes}
              skipInitialFetch={skipInitialFeeTypeFetch}
            />
          </Grid>
        )}

        {(entryType === EntryType.Time || entryType === EntryType.Cost) && (
          <>
            <Grid item xs={12} md={3}>
              <CustomController
                controllerName={`${fieldName}.duration`}
                controllerLabel="Duration"
                fieldType="text"
                placeholder="00:00"
                sx={{ my: 0 }}
                isDisabled={isDisabled}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <CustomController
                controllerName={`${fieldName}.rate`}
                controllerLabel="Rate"
                fieldType="number"
                dollarAdornment
                sx={{ my: 0 }}
                isDisabled={isDisabled}
              />
            </Grid>
          </>
        )}

        {isExpenseEntry && (
          <>
            <Grid item xs={12} md={1.5}>
              <Controller
                name={`${fieldName}.qty` as any}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    type="number"
                    fullWidth
                    label="Qty"
                    variant="outlined"
                    value={field.value ?? ''}
                    onChange={e => {
                      const v = e.target.value
                      const newQty = v === '' ? null : Number(v)
                      field.onChange(newQty)
                      const currentUnitPrice = getValues(`${fieldName}.unitPrice` as any)
                      computeAmountFromQtyUnitPrice(newQty, currentUnitPrice != null ? Number(currentUnitPrice) : null)
                    }}
                    onBlur={field.onBlur}
                    disabled={isDisabled}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={1.5}>
              <Controller
                name={`${fieldName}.unitPrice` as any}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    type="number"
                    fullWidth
                    label="Unit Price"
                    variant="outlined"
                    value={field.value ?? ''}
                    onChange={e => {
                      const v = e.target.value
                      const newUnitPrice = v === '' ? null : Number(v)
                      field.onChange(newUnitPrice)
                      const currentQty = getValues(`${fieldName}.qty` as any)
                      computeAmountFromQtyUnitPrice(currentQty != null ? Number(currentQty) : null, newUnitPrice)
                    }}
                    onBlur={field.onBlur}
                    disabled={isDisabled}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
          </>
        )}

        {entryType === EntryType.Legal && (
          <Grid item xs={12} md={3}>
            <CustomController
              controllerName={`${fieldName}.amount`}
              controllerLabel="Fee"
              fieldType="number"
              dollarAdornment
              sx={{ my: 0 }}
              isDisabled={isDisabled}
            />
          </Grid>
        )}

        <Grid item xs={12} md={isExpenseEntry ? 9 : 12}>
          <CustomController
            controllerName={`${fieldName}.description`}
            controllerLabel="Note"
            fieldType="text"
            isMultiLine={!isExpenseEntry}
            rowsLength={isExpenseEntry ? 1 : 2}
            sx={{ my: 0 }}
            isDisabled={isDisabled}
          />
        </Grid>

        {isExpenseEntry && (
          <Grid item xs={12} md={3}>
            <CustomController
              controllerName={`${fieldName}.amount`}
              controllerLabel="Amount"
              fieldType="number"
              dollarAdornment
              sx={{ my: 0 }}
              isDisabled={isDisabled}
            />
          </Grid>
        )}

        <Grid item xs={12} sx={{ ...flexCenter, ...flexJustifyEnd, flexWrap: 'wrap', columnGap: { xs: 1, sm: 5 } }}>
          <CheckboxController
            name={`${fieldName}.sendNotesToMatterNotes`}
            label="Send Notes to Matter Notes"
            disabled={isDisabled}
          />
          <CheckboxController
            name={`${fieldName}.billable`}
            label="Billable"
            disabled={isDisabled || isBillableDisabled}
          />
          <CheckboxController name={`${fieldName}.showOnBill`} label="Show on Bill" disabled={isDisabled} />
        </Grid>
      </Grid>

      <Typography minWidth={{ xs: 64, sm: 80 }} maxWidth={80} textAlign="right">
        {formatCurrency(calculateEntryTotal(currentEntry))}
      </Typography>
    </Box>
  )
}

export default EntryRowComponent
